/**
 * ImpactDashboard — /impact
 *
 * Public: "Lives Influenced" counter, program participation breakdown.
 * Private (auth-gated): personal reflection history + influence score submission.
 */
import { useEffect, useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ImpactDashboard.css';

interface Props {
  onNavigate: (path: string) => void;
}

interface ImpactSummary {
  students: number;
  hours: number;
  programs: number;
  dhanurveda_participants: number;
  samvad_participants: number;
  avg_influence_score: number;
  trips_completed: number;
}

interface Reflection {
  id: string;
  program: string;
  score: number;
  note: string;
  date: string;
}

const SUMMARY_FALLBACK: ImpactSummary = {
  students: 0, hours: 0, programs: 2,
  dhanurveda_participants: 0, samvad_participants: 0,
  avg_influence_score: 0, trips_completed: 0,
};

export default function ImpactDashboard({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const { user } = useAuth();
  const hi = locale === 'hi';

  const tl = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [summary, setSummary] = useState<ImpactSummary>(SUMMARY_FALLBACK);
  const [reflections, setReflections] = useState<Reflection[]>([]);
  const [score, setScore] = useState(7);
  const [program, setProgram] = useState('dhanurveda');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  useEffect(() => {
    fetch('/api/impact/summary')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSummary(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    user.getIdToken().then(token =>
      fetch('/api/impact/my-reflections', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.reflections) setReflections(d.reflections); })
        .catch(() => {})
    );
  }, [user]);

  const submitReflection = async () => {
    if (!user || !note.trim()) return;
    setSubmitting(true);
    setSubmitMsg('');
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/impact/reflection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ program, score, note }),
      });
      const d = await res.json();
      if (res.ok) {
        setSubmitMsg(hi ? '✅ प्रतिबिंब सहेजा गया' : '✅ Reflection saved');
        setNote('');
        setReflections(prev => [{ id: d.id || Date.now().toString(), program, score, note, date: new Date().toISOString().slice(0,10) }, ...prev]);
      } else {
        setSubmitMsg(d.error ?? (hi ? 'त्रुटि हुई' : 'Error submitting'));
      }
    } catch {
      setSubmitMsg(hi ? 'नेटवर्क त्रुटि' : 'Network error');
    }
    setSubmitting(false);
  };

  const PROGRAM_LABEL: Record<string, string> = {
    dhanurveda: hi ? 'धनुर्वेद 🏹' : 'Dhanurveda 🏹',
    samvad:     hi ? 'संस्कृत संवाद 🗣️' : 'Sanskrit Samvad 🗣️',
    trip:       hi ? 'शैक्षिक यात्रा 🏕️' : 'Educational Trip 🏕️',
  };

  return (
    <div className="id-page">
      {/* Hero */}
      <div className="id-hero">
        <span className="brand-section-label">{tl('impact.label', 'Impact & Metrics')}</span>
        <h1 className="id-hero-title">
          {hi ? 'राजधर्म — मापनीय प्रभाव' : 'Rajadharma — Measurable Impact'}
        </h1>
        <p className="id-hero-sub">
          {hi
            ? 'धनुर्वेद और संस्कृत संवाद के माध्यम से जीवन पर पड़ने वाले प्रभाव का वास्तविक समय मापन।'
            : 'Real-time measurement of the influence that Dhanurveda and Sanskrit Samvad have on lives.'}
        </p>
      </div>

      {/* Public counters */}
      <section className="id-section">
        <h2 className="id-section-title">
          📊 {hi ? 'सार्वजनिक प्रभाव सारांश' : 'Public Impact Summary'}
        </h2>
        <div className="id-counters">
          {[
            { num: summary.students,               label: hi ? 'कुल शिक्षार्थी' : 'Total Students' },
            { num: summary.dhanurveda_participants, label: hi ? 'धनुर्वेद प्रतिभागी' : 'Dhanurveda Students' },
            { num: summary.samvad_participants,     label: hi ? 'संस्कृत संवाद प्रतिभागी' : 'Samvad Students' },
            { num: summary.hours,                  label: hi ? 'अभ्यास घंटे' : 'Hours Practiced' },
            { num: summary.trips_completed,         label: hi ? 'यात्राएँ पूर्ण' : 'Trips Completed' },
            { num: summary.avg_influence_score,     label: hi ? 'औसत प्रभाव-स्कोर (१०)' : 'Avg Influence Score (10)' },
          ].map(({ num, label }) => (
            <div key={label} className="id-counter-tile">
              <span className="id-counter-num">{num}</span>
              <span className="id-counter-label">{label}</span>
            </div>
          ))}
        </div>

        {/* Program bar chart */}
        <div className="id-bar-chart">
          <div className="id-bar-row">
            <span className="id-bar-label">{hi ? 'धनुर्वेद 🏹' : 'Dhanurveda 🏹'}</span>
            <div className="id-bar-track">
              <div
                className="id-bar-fill id-bar--dh"
                style={{ width: summary.students > 0 ? `${Math.round(summary.dhanurveda_participants / summary.students * 100)}%` : '0%' }}
              />
            </div>
            <span className="id-bar-count">{summary.dhanurveda_participants}</span>
          </div>
          <div className="id-bar-row">
            <span className="id-bar-label">{hi ? 'संस्कृत संवाद 🗣️' : 'Sanskrit Samvad 🗣️'}</span>
            <div className="id-bar-track">
              <div
                className="id-bar-fill id-bar--sv"
                style={{ width: summary.students > 0 ? `${Math.round(summary.samvad_participants / summary.students * 100)}%` : '0%' }}
              />
            </div>
            <span className="id-bar-count">{summary.samvad_participants}</span>
          </div>
        </div>
      </section>

      {/* Trust message */}
      <div className="id-trust-banner">
        <span className="trust-badge">🛡️ {hi ? 'राजधर्म — सुरक्षित, विश्वसनीय, शाश्वत' : 'Rajadharma — Secure, Reliable, Timeless'}</span>
        <span className="trust-badge trust-badge--blue">🏹 {hi ? 'धनुर्वेद व संस्कृत संवाद — राजधर्म के ध्वज' : 'Dhanurveda & Samvad — pillars of Rajadharma'}</span>
      </div>

      {/* Private: reflection submission */}
      {user ? (
        <section className="id-section id-private-section">
          <h2 className="id-section-title">
            ✍️ {hi ? 'अपना प्रभाव-प्रतिबिंब जोड़ें' : 'Submit Your Influence Reflection'}
          </h2>
          <div className="id-reflection-form">
            <label className="id-form-label">{hi ? 'कार्यक्रम' : 'Program'}</label>
            <select className="id-select" value={program} onChange={e => setProgram(e.target.value)}>
              <option value="dhanurveda">{hi ? 'धनुर्वेद' : 'Dhanurveda'}</option>
              <option value="samvad">{hi ? 'संस्कृत संवाद' : 'Sanskrit Samvad'}</option>
              <option value="trip">{hi ? 'शैक्षिक यात्रा' : 'Educational Trip'}</option>
            </select>

            <label className="id-form-label">
              {hi ? `प्रभाव-स्कोर: ${score}/१०` : `Influence score: ${score}/10`}
            </label>
            <input
              type="range" min={1} max={10} value={score}
              onChange={e => setScore(Number(e.target.value))}
              className="id-range"
            />
            <div className="id-range-labels"><span>1</span><span>10</span></div>

            <label className="id-form-label">{hi ? 'प्रतिबिंब नोट' : 'Reflection note'}</label>
            <textarea
              className="id-textarea"
              rows={4}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={hi ? 'इस कार्यक्रम ने आपके जीवन को कैसे प्रभावित किया?' : 'How did this program influence your life?'}
            />

            <button
              className="id-submit-btn"
              onClick={submitReflection}
              disabled={submitting || !note.trim()}
            >
              {submitting ? (hi ? 'सहेज रहे हैं...' : 'Saving...') : (hi ? 'प्रतिबिंब सहेजें' : 'Save Reflection')}
            </button>
            {submitMsg && <p className="id-submit-msg">{submitMsg}</p>}
          </div>

          {/* Past reflections */}
          {reflections.length > 0 && (
            <div className="id-reflections-list">
              <h3 className="id-sub-title">{hi ? 'मेरे पूर्व प्रतिबिंब' : 'My Past Reflections'}</h3>
              {reflections.map(r => (
                <div key={r.id} className="id-reflection-card">
                  <div className="id-reflection-header">
                    <span className="trust-badge">{PROGRAM_LABEL[r.program] ?? r.program}</span>
                    <span className="id-reflection-score">{r.score}/10</span>
                    <span className="id-reflection-date">{r.date}</span>
                  </div>
                  <p className="id-reflection-note">{r.note}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className="id-section id-login-prompt">
          <p>
            {hi
              ? 'अपना प्रभाव-प्रतिबिंब जोड़ने के लिए लॉग इन करें।'
              : 'Sign in to submit your influence reflection and track your journey.'}
          </p>
          <button className="id-btn-primary" onClick={() => onNavigate('/profile')}>
            {hi ? '🔑 लॉग इन करें' : '🔑 Sign In'}
          </button>
        </section>
      )}
    </div>
  );
}
